/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as _ from 'lodash';

import {getEmailTemplateTeamQueryParams} from '../email-template/email-template-commons';

export {getEmailTemplateTeamQueryParams as getEmailTranslationTeamQueryParams};

const BASE_EMAIL_TYPES = [
  'PASSWORD_RESET_ACCOUNT_LOCKED',
  'PASSWORD_RESET_FORGOT_PASSWORD',
  'PASSWORD_RESET_NEW_USER',
  'PASSWORD_RESET_SIGNUP_USER',
  'PASSWORD_RESET_SIGNUP_USER_EXISTS',
  'REPORT_COMPLETE',
  'LINE_ITEM_DELETED',
  'LINE_ITEM_RUNNING',
  'LINE_ITEM_SUBMITTED',
  'LINE_ITEM_REJECTED',
  'LINE_ITEM_STOPPED_RUNNING',
  'LINE_ITEM_ENDED_END_DATE_PASSED',
  'LINE_ITEM_ENDED_OBJECTIVE_REACHED',
  'CREATIVE_REJECTED',
  'API_KEY_EXPIRING',
  'API_KEY_EXPIRED'
];

function getDefaults(defaultTranslationsByType: Record<string, any>, messageDefinition: string): Record<string, any> {
  return defaultTranslationsByType?.[messageDefinition] || {};
}

function isCustomContent(value: any, defaultValue: any): boolean {
  return _.isString(value)
    && _.trim(value).length > 0
    && _.trim(value) !== _.trim(defaultValue || '');
}

export function getEmailTranslationTypes(LocalNetworkProfile: any): string[] {
  const emailTypes = [...BASE_EMAIL_TYPES];
  if (LocalNetworkProfile.isPureMarketplacePlatform() || LocalNetworkProfile.hasPoliticalCapability()) {
    emailTypes.push('ADVERTISER_UPDATE_INVITE', 'ADVERTISER_UPDATE_INVITE_REMINDER');
  }
  return emailTypes;
}

export function updateEmailTranslationInfo(model: any, defaultTranslationsByType: Record<string, any>): void {
  model.specifyLocale = !!model.locale;
  model.specifyInterface = !!model.userInterface;
  model.specifyApplications = _.isArray(model.applications) && model.applications.length > 0;

  const defaults = getDefaults(defaultTranslationsByType, model.messageDefinition);
  model.specifyEmailSubject = isCustomContent(model.emailSubject, defaults.emailSubject);
  model.specifyEmailBody = isCustomContent(model.emailBody, defaults.emailBody);
  model.specifyEmailHeading = isCustomContent(model.emailHeading, defaults.emailHeading);
  model.specifyEmailFooter = isCustomContent(model.emailFooter, defaults.emailFooter);
}

export function applyDefaultEmailTranslationContent(model: any, defaultTranslationsByType: Record<string, any>): void {
  if (!model.messageDefinition || !defaultTranslationsByType) {
    return;
  }
  const defaults = getDefaults(defaultTranslationsByType, model.messageDefinition);
  if (!model.specifyEmailSubject) {
    model.emailSubject = defaults.emailSubject || '';
  }
  if (!model.specifyEmailBody) {
    model.emailBody = _.trim(defaults.emailBody || '');
  }
  if (!model.specifyEmailHeading) {
    model.emailHeading = _.trim(defaults.emailHeading || '');
  }
  if (!model.specifyEmailFooter) {
    model.emailFooter = _.trim(defaults.emailFooter || '');
  }
}

export function normalizeEmailTemplateSelection(model: any, saveObj?: any): void {
  const templateId = _.get(saveObj || model, ['emailTemplate', 'id']);
  if (templateId) {
    model.emailTemplate = templateId;
    return;
  }
  if (model.emailTemplate && typeof model.emailTemplate === 'object') {
    model.emailTemplate = model.emailTemplate.id;
  }
}

export function initEmailTranslationModel(model: any, defaultTranslationsByType: Record<string, any>): void {
  if (!_.isArray(model.applications)) {
    model.applications = model.applications || [];
  }
  normalizeEmailTemplateSelection(model);
  updateEmailTranslationInfo(model, defaultTranslationsByType);
  applyDefaultEmailTranslationContent(model, defaultTranslationsByType);
}

export function prepareEmailTranslationForSubmit(model: any, defaultTranslationsByType: Record<string, any>): void {
  const defaults = getDefaults(defaultTranslationsByType, model.messageDefinition);
  if (!model.specifyEmailSubject || model.emailSubject === _.get(defaults, 'emailSubject')) {
    model.emailSubject = '';
  }
  if (!model.specifyEmailBody || model.emailBody === _.get(defaults, 'emailBody')) {
    model.emailBody = '';
  }
  if (!model.specifyEmailHeading || model.emailHeading === _.get(defaults, 'emailHeading')) {
    model.emailHeading = '';
  }
  if (!model.specifyEmailFooter || model.emailFooter === _.get(defaults, 'emailFooter')) {
    model.emailFooter = '';
  }
  if (!model.specifyLocale) {
    model.locale = '';
  }
  if (!model.specifyInterface) {
    model.userInterface = '';
  }
  if (!model.specifyApplications) {
    model.applications = [];
  }
}

export function indexDefaultEmailTranslations(defaultTranslations: any[]): Record<string, any> {
  return _.keyBy(defaultTranslations, 'messageDefinition');
}
